import React, {useState, useEffect, useCallback} from 'react';
import {API} from '../../classes/API';
import {ResumeComponent} from '../../components/resume/ResumeComponent';
import {SessionHandler} from '../../components/auth';
import {User} from '@supabase/supabase-js';
import {getBearerToken} from '../../utils/auth';

// Custom hook to track image loading
function useImageLoader() {
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [totalImages, setTotalImages] = useState<number>(0);
  const [isAllImagesLoaded, setIsAllImagesLoaded] = useState<boolean>(false);
  const [trackedImages, setTrackedImages] = useState<Set<string>>(new Set());
  const [isTrackingComplete, setIsTrackingComplete] = useState<boolean>(false);

  const trackImage = useCallback(
    (src: string) => {
      // Prevent tracking the same image multiple times
      if (trackedImages.has(src)) {
        return;
      }

      console.log('🔍 Tracking image:', src);

      setTrackedImages(prev => new Set(prev).add(src));
      setTotalImages(prev => {
        const newTotal = prev + 1;
        console.log('📈 Total images to track:', newTotal);
        return newTotal;
      });

      // Check if image is already loaded (cached)
      const img = new Image();
      img.onload = () => {
        console.log('✅ Image loaded:', src);
        setLoadedImages(prev => {
          const newSet = new Set(prev);
          newSet.add(src);
          const newLoaded = newSet.size;
          console.log(`📊 Loaded: ${newLoaded}/${totalImages + 1}`);
          return newSet;
        });
      };
      img.onerror = () => {
        console.log('❌ Image failed:', src);
        // Even if image fails to load, count it as "loaded" to prevent infinite waiting
        setLoadedImages(prev => {
          const newSet = new Set(prev);
          newSet.add(src);
          const newLoaded = newSet.size;
          console.log(
            `📊 Loaded (including failed): ${newLoaded}/${totalImages + 1}`,
          );
          return newSet;
        });
      };

      // Set src after setting up event handlers
      img.src = src;

      // If the image is already cached, the onload event might fire immediately
      // or not at all, so we need a fallback
      if (img.complete) {
        console.log('⚡ Image cached:', src);
        setLoadedImages(prev => {
          const newSet = new Set(prev);
          newSet.add(src);
          const newLoaded = newSet.size;
          console.log(`📊 Loaded (cached): ${newLoaded}/${totalImages + 1}`);
          return newSet;
        });
      }
    },
    [trackedImages, totalImages],
  );

  useEffect(() => {
    if (totalImages > 0 && loadedImages.size === totalImages) {
      console.log('🎉 All images loaded!');
      setIsAllImagesLoaded(true);
      setIsTrackingComplete(true);
    } else if (totalImages === 0) {
      // If no images are found, consider images as loaded
      console.log('🚫 No images found, considering images as loaded');
      setIsAllImagesLoaded(true);
      setIsTrackingComplete(true);
    }
  }, [loadedImages.size, totalImages]);

  // Fallback timeout to prevent infinite waiting (only if tracking is not complete)
  useEffect(() => {
    if (totalImages > 0 && !isTrackingComplete) {
      const timeout = setTimeout(() => {
        console.log('⏰ Image loading timeout reached, forcing ready state');
        setIsAllImagesLoaded(true);
        setIsTrackingComplete(true);
      }, 30000); // 30 second timeout

      return () => clearTimeout(timeout);
    }
  }, [totalImages, isTrackingComplete]);

  return {
    trackImage,
    isAllImagesLoaded,
    totalImages,
    loadedImages,
    trackedImages,
  };
}

// Custom hook to manage overall page readiness
function usePageReadiness() {
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [isImagesLoaded, setIsImagesLoaded] = useState<boolean>(false);
  const [isFontsLoaded, setIsFontsLoaded] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);

  useEffect(() => {
    // Check if fonts are loaded
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setIsFontsLoaded(true);
      });
    } else {
      // Fallback for browsers that don't support document.fonts
      setIsFontsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isDataLoaded && isImagesLoaded && isFontsLoaded) {
      // Add a small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        console.log('Page is now ready for printing!');
        setIsReady(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      console.log('Page readiness status:', {
        isDataLoaded,
        isImagesLoaded,
        isFontsLoaded,
      });
    }
  }, [isDataLoaded, isImagesLoaded, isFontsLoaded]);

  return {
    isReady,
    setIsDataLoaded,
    setIsImagesLoaded,
    isDataLoaded,
    isImagesLoaded,
    isFontsLoaded,
  };
}

// Error display component
function ErrorDisplay({error}: {error: string}): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
          <svg
            className="w-6 h-6 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <div className="mt-4 text-center">
          <h3 className="text-lg font-medium text-gray-900">Error</h3>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
        </div>
      </div>
    </div>
  );
}

// Loading display component with progress
function LoadingDisplay({
  isDataLoaded,
  isImagesLoaded,
  isFontsLoaded,
  totalImages,
  loadedImages,
}: {
  isDataLoaded: boolean;
  isImagesLoaded: boolean;
  isFontsLoaded: boolean;
  totalImages: number;
  loadedImages: Set<string>;
}): JSX.Element {
  const progress = Math.round(
    (((isDataLoaded ? 1 : 0) +
      (isImagesLoaded ? 1 : 0) +
      (isFontsLoaded ? 1 : 0)) /
      3) *
      100,
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-blue-100 rounded-full">
          <svg
            className="w-6 h-6 text-blue-600 animate-spin"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </div>
        <div className="mt-4 text-center">
          <h3 className="text-lg font-medium text-gray-900">
            Preparing Print Page
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Loading profile data and assets...
          </p>

          {/* Progress bar */}
          <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{width: `${progress}%`}}></div>
          </div>

          {/* Status indicators */}
          <div className="mt-4 space-y-2 text-xs text-gray-600">
            <div
              className={`flex items-center justify-between ${
                isDataLoaded ? 'text-green-600' : 'text-gray-400'
              }`}>
              <span>Profile Data</span>
              <span>{isDataLoaded ? '✓' : '...'}</span>
            </div>
            <div
              className={`flex items-center justify-between ${
                isFontsLoaded ? 'text-green-600' : 'text-gray-400'
              }`}>
              <span>Fonts</span>
              <span>{isFontsLoaded ? '✓' : '...'}</span>
            </div>
            <div
              className={`flex items-center justify-between ${
                isImagesLoaded ? 'text-green-600' : 'text-gray-400'
              }`}>
              <span>
                Images ({loadedImages.size}/{totalImages})
              </span>
              <span>{isImagesLoaded ? '✓' : '...'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PrintPage(): JSX.Element {
  const api = API.getInstance({persistSession: false});
  const [token, setToken] = useState<string | undefined>(getBearerToken());
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [isDOMStable, setIsDOMStable] = useState<boolean>(false);

  // Custom hooks for tracking readiness
  const {
    trackImage,
    isAllImagesLoaded,
    totalImages,
    loadedImages,
    trackedImages,
  } = useImageLoader();
  const {
    isReady,
    setIsDataLoaded,
    setIsImagesLoaded,
    isDataLoaded,
    isImagesLoaded,
    isFontsLoaded,
  } = usePageReadiness();

  // DOM stability callback
  const handleDOMStable = useCallback(() => {
    setIsDOMStable(true);
  }, []);

  // Update token when component mounts
  useEffect(() => {
    setToken(getBearerToken());
  }, []);

  // Load profile data
  const loadProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!token) {
        throw new Error(
          'No authorization token found. Please provide a Bearer token via Authorization header or URL parameter.',
        );
      }

      const profileId = await api.getProfileIdFromToken(token);

      const {
        data: {user},
      } = await api.supabaseClient.auth.getUser(token);
      setUser(user);
      setProfileId(profileId);
      setIsDataLoaded(true);
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : 'Failed to load profile';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [api, token, setIsDataLoaded]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile, api]);

  // Update image loading state
  useEffect(() => {
    console.log('🔄 Image loading state updated:', {
      isAllImagesLoaded,
      totalImages,
      loadedImagesCount: loadedImages.size,
      trackedImagesCount: trackedImages.size,
    });

    // Log final summary when all images are loaded
    if (isAllImagesLoaded && totalImages > 0) {
      console.log('📋 Final Image Summary:', {
        totalImages,
        loadedImages: Array.from(loadedImages),
        trackedImages: Array.from(trackedImages),
      });
    }

    setIsImagesLoaded(isAllImagesLoaded);
  }, [
    isAllImagesLoaded,
    setIsImagesLoaded,
    totalImages,
    loadedImages.size,
    trackedImages.size,
    loadedImages,
    trackedImages,
  ]);

  // Debug: Log when trackImage is passed to ResumeComponent
  useEffect(() => {
    console.log(
      '🎯 trackImage function created and ready to be passed to components',
    );
  }, [trackImage]);

  // Add ready state to window for Puppeteer to detect
  useEffect(() => {
    // Only mark as ready when all components are ready AND DOM is stable
    if (isReady && isDOMStable) {
      // Add a data attribute to the body for Puppeteer to detect
      document.body.setAttribute('data-print-ready', 'true');

      // Add a global variable for Puppeteer to check
      (window as {printPageReady?: boolean}).printPageReady = true;

      setTimeout(() => {
        // Also dispatch a custom event
        window.dispatchEvent(new CustomEvent('printPageReady'));
      }, 1000);

      // DO NOT CHANGE THIS LOG MESSAGE
      // This is used by Puppeteer to detect when the page is ready
      console.log('PrintPage is ready for printing');
    } else {
      document.body.removeAttribute('data-print-ready');
      (window as {printPageReady?: boolean}).printPageReady = false;
    }
  }, [isReady, isDOMStable]);

  // Show error state
  if (error) {
    return <ErrorDisplay error={error} />;
  }

  // Show loading state
  if (isLoading || !isReady) {
    return (
      <LoadingDisplay
        isDataLoaded={isDataLoaded}
        isImagesLoaded={isImagesLoaded}
        isFontsLoaded={isFontsLoaded}
        totalImages={totalImages}
        loadedImages={loadedImages}
      />
    );
  }

  // Show profile data
  return (
    <SessionHandler
      token={token}
      persistSession={false}
      loadingComponent={
        <LoadingDisplay
          isDataLoaded={isDataLoaded}
          isImagesLoaded={isImagesLoaded}
          isFontsLoaded={isFontsLoaded}
          totalImages={totalImages}
          loadedImages={loadedImages}
        />
      }
      errorComponent={<ErrorDisplay error="No profile data available" />}>
      <div className="w-[210mm] mx-auto">
        {profileId ? (
          <ResumeComponent
            profileId={profileId}
            user={user}
            isForPDF={true}
            onImageLoad={trackImage}
            onDOMStable={handleDOMStable}
          />
        ) : (
          <ErrorDisplay error="No profile data available" />
        )}
      </div>
    </SessionHandler>
  );
}
